/**
 * Contest-specific marketing copy for the Smart Optimizer.
 *
 * The Smart Optimizer runs a different ruleset per contest type (see
 * `backend/contest_sizing.py::derive_config`). This module maps each
 * preset to its own confidence-building copy so users see language
 * that matches the strategy they've chosen — a milly_maker user reads
 * "millionaire blueprint" lingo, a cash_50_50 user reads "floor
 * fortress" lingo, etc. Cross-contamination breaks trust.
 *
 * `key`   — matches backend preset key (backend/contest_sizing.py)
 * `label` — short marketing label for the confidence block
 * `headline` — the one big sentence
 * `sub`   — supporting sentence
 * `pillars` — 3 short feature bullets rendered as chips
 */
export const CONTEST_MARKETING = {
  milly_maker: {
    tier: "MILLIONAIRE BLUEPRINT",
    tone: "trophy",
    label: "Milly Maker Mode",
    headline: "Built to win Sunday's Milly Maker.",
    sub: "The engine models what the field's optimizers will build this week, keeps every one of your entries clear of those lineups, and ranks your portfolio by how often it beats the field in correlated simulation — not by projected points.",
    pillars: [
      { name: "Field model",        desc: "The consensus of the sites the crowd uses becomes the shadow field your lineups step around" },
      { name: "Copies cap",         desc: "Expected duplicates per lineup capped for a 200k field — leverage anchors in every build" },
      { name: "Field-Win ranking",  desc: "Correlated game scripts rank each lineup by its chance to beat the field, net of copies" },
    ],
  },
  "20_max": {
    tier: "FIELD-BEATER FRAMEWORK",
    tone: "target",
    label: "20-Max GPP Mode",
    headline: "Built for leverage. Tuned for the ceiling.",
    sub: "Standard optimizers put you in 500 identical lineups. Ours forces the pivots the field will fade — where the tournament money actually lives.",
    pillars: [
      { name: "Pivot Engine",        desc: "Auto-forces low-owned leverage plays that break duplicate risk" },
      { name: "Ceiling Physics",     desc: "Every lineup targets a top-1% game script, not the median" },
      { name: "Correlation Cascade", desc: "Multi-layer stack topology that compounds when your slate hits" },
    ],
  },
  three_max: {
    tier: "FIELD-BEATER FRAMEWORK",
    tone: "target",
    label: "3-Max Mode",
    headline: "Three shots. Three different game scripts. Zero overlap.",
    sub: "3-Max isn't 20-Max with fewer entries — it's a completely different math problem. Every entry gets its own ceiling story, forced by the engine.",
    pillars: [
      { name: "Diversity Locking",   desc: "Guarantees each entry hits a distinct game-script hypothesis" },
      { name: "Pivot Engine",        desc: "Low-owned pivots forced into every lineup for leverage" },
      { name: "Ceiling Physics",     desc: "Optimizes for the 99th percentile, not the average" },
    ],
  },
  single_entry_150: {
    tier: "FIELD-BEATER FRAMEWORK",
    tone: "target",
    label: "Single-Entry Mode",
    headline: "One shot. Zero regrets.",
    sub: "Single-entry tournaments punish duplication and reward the boldest ceiling play. Our engine builds the exact lineup you'd have played if you'd stared at the slate all week.",
    pillars: [
      { name: "Ceiling Physics",     desc: "Optimizes for one 99th-percentile outcome, not consistency" },
      { name: "Pivot Engine",        desc: "Injects the low-owned play the field will look back on and regret fading" },
      { name: "Correlation Engine",  desc: "Every skill spot has a story — stacked, run-back, or contrarian" },
    ],
  },
  small_gpp: {
    tier: "FIELD-BEATER FRAMEWORK",
    tone: "target",
    label: "Small-Field GPP Mode",
    headline: "Smaller field. Different physics. Same edge.",
    sub: "Sub-5k field GPPs reward chalk far more than milly maker will. Our engine relaxes uniqueness while keeping the correlation locked — you get the winning stacks without the low-own noise.",
    pillars: [
      { name: "Chalk-Aware Stacking", desc: "Keeps the correlation math but stops forcing low-own pivots that don't pay in small fields" },
      { name: "Correlation Engine",   desc: "QB + WR + run-back locked in, tuned to <5k field topology" },
      { name: "Floor-to-Ceiling Blend", desc: "50/50 leverage vs safety — matches how small GPPs actually pay" },
    ],
  },
  flea_flicker: {
    tier: "FIELD-BEATER FRAMEWORK",
    tone: "target",
    label: "Flea Flicker Mode",
    headline: "Mid-field GPP. Full ceiling. Zero fluff.",
    sub: "The 50k-entry Flea Flicker sits in the awkward middle — too big for chalk, too small for full milly leverage. Our ruleset splits the difference, calibrated on the exact field size.",
    pillars: [
      { name: "Mid-Field Barbell",   desc: "Balances chalk floors with mandatory sub-10% ceiling plays" },
      { name: "Correlation Engine",  desc: "Full stack topology with tuned bring-back rate for the field size" },
      { name: "Pivot Engine",        desc: "Forces the leverage plays without over-forcing rare pivots" },
    ],
  },
  cash_50_50: {
    tier: "CASH GAME PROTOCOL",
    tone: "shield",
    label: "Cash 50-50 Mode",
    headline: "Chalk is not the enemy. Losing is.",
    sub: "Cash games reward the boring. We remove the stacking noise, embrace the obvious plays, and build the highest-median roster on the slate every single time.",
    pillars: [
      { name: "Floor Fortress",      desc: "Every roster spot is a 65th-percentile-or-better play. No ceiling gambles." },
      { name: "Chalk Confidence",    desc: "High-owned isn't scary in cash — it's usually the winning move" },
      { name: "Median Machine",      desc: "Optimizes for the 50th-percentile outcome, not the top-1%" },
    ],
  },
  head_to_head: {
    tier: "CASH GAME PROTOCOL",
    tone: "shield",
    label: "Head-to-Head Mode",
    headline: "Beat one person. Bank the profit.",
    sub: "H2H is cash with even less variance tolerance. The engine pulls the ceiling levers all the way off and stacks nothing but the highest-median builds.",
    pillars: [
      { name: "Floor Fortress",      desc: "Zero volatility plays. Every seat is a boring, high-median lock." },
      { name: "Chalk Confidence",    desc: "Play the obvious guys. In H2H, the field is one opponent." },
      { name: "Percentile Play",     desc: "50th-percentile score beats 99% of H2H opponents on any given slate" },
    ],
  },
  showdown: {
    tier: "SINGLE-GAME COMBAT",
    tone: "captain",
    label: "Showdown Mode",
    headline: "Captain math, solved.",
    sub: "Showdown lives and dies on captain selection and game-script correlation. Our engine models both explicitly — no more picking captains by gut.",
    pillars: [
      { name: "Captain Calculus",    desc: "1.5x multiplier math folded into leverage — the field always picks the wrong captain" },
      { name: "Game Script Engine",  desc: "Blowout, shootout, methodical — different builds for different scripts" },
      { name: "Runback Physics",     desc: "Forces the correlated bring-back that pays when the game goes over" },
    ],
  },
};

/**
 * Fallback pack for any preset we haven't authored copy for yet.
 * Keeps the UI stable if the backend ships a new preset before the
 * marketing pack is updated. Deliberately generic so it never claims
 * the wrong strategy for the wrong contest.
 */
export const CONTEST_MARKETING_FALLBACK = {
  tier: "SMART OPTIMIZER",
  tone: "target",
  label: "Optimizer",
  headline: "Built for winners.",
  sub: "Rules and constraints tuned to the exact contest you're playing.",
  pillars: [],
};

export function getContestMarketing(presetKey) {
  return CONTEST_MARKETING[presetKey] || CONTEST_MARKETING_FALLBACK;
}
